// Connected data sources (docs/workflows-round2-plan.md §6): a database, a
// dataset file or URL, a data API, or market data, connected once by the operator,
// DISCOVERED by lattice in the background (tables, sizes, time ranges,
// samples) and DESCRIBED by the customer's model, then used by agents and
// workflow stages through lattice, which keeps the credentials (a password
// header or API key is write-only: views carry `has_password` / `has_headers`
// / `has_api_key`). A data API is not SQL: agents `fetch` its endpoints, and
// the operator tries one call with the fetch preview.
// Routes: routes/data_sources.py. Refusals are `{detail: {code, message}}`
// like the workflows routes, so `workflowError` words them.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

/**
 * Every kind a source can be; "seafile" and "python" (a Python library Corvus
 * wraps and installs) ahead of api.gen.ts. A kind this build does not know
 * still renders (dataModel.kindMeta).
 */
export type DataSourceKind = components["schemas"]["DataSourceView"]["kind"] | "seafile" | "python";
export type DataSourceStatus = components["schemas"]["DataSourceView"]["status"];
/** A Python library source's package (a PyPI name, `name==version` or its link): optional until api.gen.ts has it. */
interface PackageField {
  package?: string | null;
}
export type DataSourceView = Omit<components["schemas"]["DataSourceView"], "kind"> & { kind: DataSourceKind } & PackageField;
export type DataSourceDetail = Omit<components["schemas"]["DataSourceDetail"], "kind"> & { kind: DataSourceKind } & PackageField;
export type DataSourceList = Omit<components["schemas"]["DataSourceList"], "sources" | "kinds"> & { sources: DataSourceView[]; kinds: DataSourceKind[] };
export type DataCatalog = components["schemas"]["Catalog"];
export type TableProfile = components["schemas"]["TableProfile"];
export type ColumnProfile = components["schemas"]["ColumnProfile"];
export type ConnectionSpec = components["schemas"]["ConnectionSpec"] & PackageField;
export type CreateDataSourceBody = Omit<components["schemas"]["CreateDataSourceBody"], "kind"> & { kind: DataSourceKind };
export type UpdateDataSourceBody = components["schemas"]["UpdateDataSourceBody"];
export type TestConnectionBody = components["schemas"]["TestConnectionBody"];
/** `needs_host`: the host the workspace must allow before it can connect (additive; optional until api.gen.ts has it). */
export type TestConnectionResponse = components["schemas"]["TestConnectionResponse"] & { needs_host?: string | null };
/** Why a subagent may use a source: a saved workflow's stage or a job's team (DataSourceDetail.needed_by). */
export type SourceUse = components["schemas"]["SourceUse"];
export type QueryBody = components["schemas"]["QueryBody"];
export type QueryResult = components["schemas"]["QueryResult"];
/** A data API's endpoint, as its own description gives it (DataCatalog.endpoints). */
export type EndpointProfile = components["schemas"]["EndpointProfile"];
export type EndpointParam = components["schemas"]["EndpointParam"];
export type FetchPreviewBody = components["schemas"]["FetchPreviewBody"];
export type FetchPreview = components["schemas"]["FetchPreview"];

const base = (c: string) => `/api/customers/${encodeURIComponent(c)}/data-sources`;
const one = (c: string, name: string) => `${base(c)}/${encodeURIComponent(name)}`;

export const dataSourceKeys = {
  all: (c: string | null) => ["data-sources", c] as const,
  list: (c: string | null) => ["data-sources", c, "list"] as const,
  one: (c: string | null, name: string | null) => ["data-sources", c, "one", name] as const,
};

const notFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/** Discovery is a background job: a source in this state is re-read until it settles. */
export const isDiscovering = (status: DataSourceStatus | undefined) => status === "discovering";

/** Every source of the workspace, the kinds it offers and the operator's extra allowed hosts. Re-read every 2 s while one discovers. */
export function useDataSources(customerId: string | null) {
  return useQuery({
    queryKey: dataSourceKeys.list(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<DataSourceList>(base(customerId!), { signal }),
    staleTime: 5_000,
    refetchInterval: (q) => ((q.state.data?.sources ?? []).some((s) => isDiscovering(s.status)) ? 2_000 : 30_000),
  });
}

/** One source with its catalog; re-read every 2 s while it discovers. */
export function useDataSource(customerId: string | null, name: string | null) {
  return useQuery({
    queryKey: dataSourceKeys.one(customerId, name),
    enabled: !!customerId && !!name,
    queryFn: ({ signal }) => apiFetch<DataSourceDetail>(one(customerId!, name!), { signal }),
    staleTime: 2_000,
    refetchInterval: (q) => (isDiscovering(q.state.data?.status) ? 2_000 : false),
    retry: (count, error) => !notFound(error) && count < 1,
  });
}

function invalidate(qc: QueryClient, customerId: string, name?: string) {
  void qc.invalidateQueries({ queryKey: dataSourceKeys.list(customerId) });
  if (name) void qc.invalidateQueries({ queryKey: dataSourceKeys.one(customerId, name) });
}

function saved(qc: QueryClient, customerId: string, view: DataSourceDetail | DataSourceView) {
  if ("catalog" in view) qc.setQueryData(dataSourceKeys.one(customerId, view.name), view);
  invalidate(qc, customerId, view.name);
}

export function useCreateDataSource() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, body }: { customerId: string; body: CreateDataSourceBody }) =>
      apiFetch<DataSourceDetail>(base(customerId), { method: "POST", body }),
    onSuccess: (view, vars) => saved(qc, vars.customerId, view),
  });
}

/**
 * Title, description, connection (a blank password keeps the stored one;
 * a changed one is discovered again unless `discover: false`), who may use
 * it (`[]` = nobody yet), hidden tables.
 */
export function useUpdateDataSource() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, body }: { customerId: string; name: string; body: UpdateDataSourceBody }) =>
      apiFetch<DataSourceDetail>(one(customerId, name), { method: "PATCH", body }),
    onSuccess: (view, vars) => saved(qc, vars.customerId, view),
  });
}

/** 409 `in_use` while a workflow names it (the message lists them). */
export function useDeleteDataSource() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name }: { customerId: string; name: string }) => apiFetch<void>(one(customerId, name), { method: "DELETE" }),
    onSuccess: (_d, vars) => {
      qc.removeQueries({ queryKey: dataSourceKeys.one(vars.customerId, vars.name) });
      invalidate(qc, vars.customerId);
    },
  });
}

/** The stored connection, or with `body` the typed one — tried without saving it (the edit form's Test). */
export function useTestDataSource() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, body }: { customerId: string; name: string; body?: TestConnectionBody }) =>
      apiFetch<TestConnectionResponse>(`${one(customerId, name)}/test`, { method: "POST", body }),
  });
}

/** Starts (or restarts) discovery; the view then reads `discovering` until it settles. */
export function useDiscoverDataSource() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name }: { customerId: string; name: string }) =>
      apiFetch<DataSourceView>(`${one(customerId, name)}/discover`, { method: "POST" }),
    onSuccess: (view, vars) => {
      qc.setQueryData<DataSourceDetail | undefined>(dataSourceKeys.one(vars.customerId, vars.name), (old) => (old ? { ...old, ...view } : old));
      invalidate(qc, vars.customerId, vars.name);
    },
  });
}

/** Read-only SQL, one statement, capped rows (the server enforces all three). */
export function useQueryDataSource() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, body }: { customerId: string; name: string; body: QueryBody }) =>
      apiFetch<QueryResult>(`${one(customerId, name)}/query`, { method: "POST", body }),
  });
}

/** A data API's try-it: one call (Lattice adds the key), the first rows or the text back, nothing saved. */
export function useFetchPreview() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, body }: { customerId: string; name: string; body: FetchPreviewBody }) =>
      apiFetch<FetchPreview>(`${one(customerId, name)}/fetch-preview`, { method: "POST", body }),
  });
}

/** The operator's own hosts, beside the private, tailnet and loopback ranges that are always allowed. */
export function useSetAllowedHosts() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, hosts }: { customerId: string; hosts: string[] }) =>
      apiFetch<DataSourceList>(`/api/customers/${encodeURIComponent(customerId)}/data-sources-allowed-hosts`, { method: "PUT", body: { hosts } }),
    onSuccess: (list, vars) => qc.setQueryData(dataSourceKeys.list(vars.customerId), list),
  });
}

/** Adds one host to the workspace's list: read fresh first, so a host added meanwhile stays. */
export function useAllowHost() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async ({ customerId, host }: { customerId: string; host: string }) => {
      const now = await apiFetch<DataSourceList>(base(customerId));
      const hosts = now.allowed_hosts ?? [];
      if (hosts.some((h) => h.toLowerCase() === host.toLowerCase())) return now;
      return apiFetch<DataSourceList>(`/api/customers/${encodeURIComponent(customerId)}/data-sources-allowed-hosts`, { method: "PUT", body: { hosts: [...hosts, host] } });
    },
    onSuccess: (list, vars) => qc.setQueryData(dataSourceKeys.list(vars.customerId), list),
  });
}

/**
 * The file of a sqlite / duckdb / file source (multipart field `file`),
 * with upload progress (0..1) — fetch cannot report it, so this is an XHR.
 */
export function uploadDataSourceFile(
  { customerId, name, file }: { customerId: string; name: string; file: File },
  onProgress?: (fraction: number) => void,
): Promise<DataSourceDetail> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${one(customerId, name)}/upload`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, "The upload did not reach the server.", null));
    xhr.onload = () => {
      let parsed: unknown = null;
      if (xhr.responseText) {
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = { error: "non_json_response", snippet: xhr.responseText.slice(0, 400) };
        }
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(parsed as DataSourceDetail);
      else reject(new ApiError(xhr.status, `upload failed: ${xhr.status}`, parsed));
    };
    const form = new FormData();
    form.append("file", file, file.name);
    xhr.send(form);
  });
}

export function useUploadDataSourceFile() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (vars: { customerId: string; name: string; file: File; onProgress?: (fraction: number) => void }) => uploadDataSourceFile(vars, vars.onProgress),
    onSuccess: (view, vars) => saved(qc, vars.customerId, view),
  });
}

/** Remove one file of a dataset source (the rest are discovered again); 404 `no_file` when it is not there. */
export function useDeleteDataSourceFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, name, file }: { customerId: string; name: string; file: string }) =>
      apiFetch<DataSourceDetail>(`${one(customerId, name)}/files/${encodeURIComponent(file)}`, { method: "DELETE" }),
    onSuccess: (view, vars) => saved(qc, vars.customerId, view),
  });
}

/** The workflows a 409 `in_use` names (`detail.workflows`), when the refusal carries them. */
export function inUseWorkflows(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.status !== 409) return [];
  const detail = (error.body as { detail?: { workflows?: unknown } } | null)?.detail;
  return Array.isArray(detail?.workflows) ? detail.workflows.map(String) : [];
}
